import React, { type JSX } from 'react';

import CustomIcon from '../../../../components/CustomIcon';
import { WidgetHeader } from 'pages/WidgetManagement/common/WidgetHeader';
import { InfoListItem } from '@brightlayer-ui/react-components';
import HelpCenter from '@mui/icons-material/HelpCenter';
import Workspaces from '@mui/icons-material/Workspaces';
import { Avatar, Button, Divider, List, Paper, Stack, Typography } from '@mui/material';
import { VIEW_ALL_FOOTER, WidgetFooter } from 'pages/WidgetManagement/common';
import { CustomTransComponent, GroupPath } from 'components';
import { useNavigate } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { NotificationsActive, Warning } from '@mui/icons-material';
import { CloudOffFilled } from '@brightlayer-ui/icons-mui';
import { useTranslation } from 'react-i18next';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC } from 'hooks';
import { useTheme } from '@mui/material/styles';
import Add from '@mui/icons-material/Add';
import { useAppDispatch } from '@fiji/common/src/app/store';
import { addGroupModal } from '@fiji/common/src/features/group/groupSlice';

const renderAddGroupActions = (
    canCreateGroup: boolean,
    theme: any,
    handleOpenAddGroupModal: () => any
): JSX.Element => (
    <>
        {canCreateGroup && (
            <Button
                id="addGroups"
                sx={{
                    border: `1px solid ${theme?.palette?.primary?.main}`,
                    '&:hover': {
                        backgroundColor: theme?.palette?.primary?.[50],
                        color: theme?.palette?.primary?.main,
                    },
                }}
                variant={'outlined'}
                color={'primary'}
                startIcon={<Add />}
                onClick={(): void => handleOpenAddGroupModal()}
            >
                <CustomTransComponent translationKey={'COMMON:ADD_GROUP'} />
            </Button>
        )}
    </>
);

const openAddGroupModal = (dispatch: ReturnType<typeof useAppDispatch>, groupId?: string): any =>
    dispatch(addGroupModal({ isOpen: true, group: groupId ?? '' }));

const COUNT_KEYS = ['alarmCount', 'warningCount', 'offlineCount'];

const Component = ({ widgetData: detailWidget, mode }: { widgetData: any; mode: string }): JSX.Element => {
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const theme: any = useTheme();
    const dispatch = useAppDispatch();
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canViewGroups = hasPermission('group-management_view-group-management');
    const canViewGroupsExpired = hasPermission('group-management_view-group-management_hasExpired');
    const canCreateGroup = hasPermission('group-management_create-groups');

    const colors: any = {
        warningCount: '#EEA31B',
        offlineCount: '#69B1C3',
        alarmCount: '#CA3C3D',
    };

    const navigate = useNavigate();
    const { t } = useTranslation();
    const selectedAssetCount =
        (detailWidget?.filters?.groupIds?.length ?? 0) + (detailWidget?.filters?.gatewayId?.length ?? 0);
    const maxItems = detailWidget?.maxCount ?? detailWidget?.secondary?.length;

    const getIcon = (status: string): JSX.Element => {
        if (status === 'alarmCount') return <NotificationsActive sx={{ color: colors[status] }} fontSize={'inherit'} />;
        if (status === 'offlineCount') return <CloudOffFilled sx={{ color: colors[status] }} fontSize={'inherit'} />;
        if (status === 'warningCount') return <Warning sx={{ color: colors[status] }} fontSize={'inherit'} />;
        return <React.Fragment />;
    };

    const getIconData = (count: number, status: string): JSX.Element =>
        count ? (
            <Stack key={status} flexDirection={'row'} alignItems={'center'} sx={{ mr: 2 }}>
                {getIcon(status)}
                <Typography
                    sx={{
                        color: colors[status],
                        fontSize: '14px',
                        fontWeight: '600',
                    }}
                >
                    {count}
                </Typography>
            </Stack>
        ) : (
            <></>
        );
    const handleOpenAddGroupModal = (groupId?: string): any => openAddGroupModal(dispatch, groupId);
    return (
        <Paper sx={{ height: '100%' }}>
            <WidgetHeader
                mode={mode}
                widgetData={detailWidget}
                count={detailWidget?.multiDevice ? selectedAssetCount : detailWidget?.total}
            />
            {mode !== 'edit' && <Divider />}
            <Stack
                sx={{
                    height: `calc(100% - ${mode === 'edit' || detailWidget?.viewAllButton ? '97px' : '49px'})`,
                    overflow: 'auto',
                }}
            >
                {detailWidget?.secondary?.length ? (
                    detailWidget?.secondary?.slice(0, maxItems).map((item: any) => (
                        <List key={item?.id} disablePadding>
                            <InfoListItem
                                title={''}
                                divider={'full'}
                                avatar
                                iconAlign="center"
                                statusColor={item?.indicator}
                                sx={{
                                    height: '100%',
                                    '& .MuiListItemButton-root': { height: '100%' },
                                }}
                                leftComponent={
                                    <Stack className="white-space-pre" style={{ width: '100%' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: '#424E54',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '74px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item?.name}
                                        </Typography>
                                        <GroupPath pathName={item?.groupPath} width={'80%'} />
                                    </Stack>
                                }
                                icon={
                                    <Avatar sx={{ bgcolor: item?.icon?.web?.style?.backgroundColor }}>
                                        <CustomIcon
                                            iconName={item.icon?.web?.name ?? ''}
                                            family={item.icon?.web?.family ?? ''}
                                            iconProps={{ sx: { color: item?.icon?.web?.style?.color } }}
                                            defaultIcon={<HelpCenter color="primary" />}
                                        />
                                    </Avatar>
                                }
                                rightComponent={
                                    <Stack flexDirection={'column'} alignItems={'center'}>
                                        <Stack flexDirection={'row'} alignItems={'center'}>
                                            {COUNT_KEYS?.map((subItem: string) => getIconData(item[subItem], subItem))}
                                        </Stack>
                                        {Boolean(item['deviceCount']) && (
                                            <Typography
                                                sx={{
                                                    marginTop: '8px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                }}
                                            >
                                                {`${item['deviceCount']} ${
                                                    item['deviceCount'] > 1 ? 'Devices' : 'Device'
                                                } `}
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                                chevron
                                ripple
                                onClick={(): void => {
                                    if (mode !== 'view') return;
                                    if (item?.type !== 'GROUP') {
                                        navigate(`/${currentRealmName}/device/${item?.id}`);
                                        return;
                                    }
                                    // Leaf groups (no child groups) go straight to the Group Summary Dashboard;
                                    // groups with children drill down through the sub-group list.
                                    const groupPath = item?.groupCount
                                        ? `/${currentRealmName}/groupDetails/${item.id}`
                                        : `/${currentRealmName}/group/${item.id}`;
                                    navigate(groupPath);
                                }}
                            />
                        </List>
                    ))
                ) : (
                    <CustomEmptyState
                        className="padding-y-96"
                        icon={<Workspaces fontSize={'inherit'} />}
                        title={<CustomTransComponent translationKey={'GROUP_MANAGEMENT:LABELS.NO_GROUPS'} />}
                        actions={renderAddGroupActions(canCreateGroup, theme, handleOpenAddGroupModal)}
                    />
                )}
            </Stack>
            {(canViewGroups || canViewGroupsExpired) && (
                <WidgetFooter
                    actionBtnName={`${t('COMMON:VIEW_ALL')} ${VIEW_ALL_FOOTER['group_list']}`}
                    mode={mode}
                    widgetData={detailWidget}
                />
            )}
        </Paper>
    );
};
export default Component;
